<template>
    <div class="virtual-select-container" :style="{ width: width + 'px' }">
        <el-select
            ref="select"
            v-model="Datas"
            :placeholder="placeholders"
            :clearable="clearables"
            :multiple="multiple"
            :disabled="disable"
            filterable
            remote
            :remote-method="handleSearch"
            :loading="loading"
            @change="handleChange"
            @visible-change="handleVisibleChange"
            @hook:mounted="initSelect"
            class="virtual-select"
            popper-class="virtual-select-popper"
        >
            <el-option
                v-for="item in visibleItems"
                :key="getItemKey(item)"
                :label="getOptionLabel(item)"
                :value="getItemValue(item)"
                :disabled="isItemDisabled(item)"
            >
                <div class="option-content">
                    <span class="option-value" :class="{ locked: isItemLocked(item) }">
                        <el-tag v-if="isItemLocked(item)" size="mini" type="danger">锁定</el-tag>
                        {{ getItemValueText(item) }}
                    </span>
                    <span v-if="!single" class="option-label">{{ getItemLabelText(item) }}</span>
                </div>
            </el-option>
        </el-select>
    </div>
</template>

<script>
export default {
    name: 'VirtualSelect',
    data() {
        return {
            Datas: '',
            // 原始数据
            rawData: [],
            // 当前显示的数据
            visibleItems: [],
            // 搜索结果
            searchResults: [],
            // 加载状态
            loading: false,
            // 当前是否在搜索模式
            isSearching: false,
            // 防抖定时器
            searchTimer: null,
            // 缓存的搜索结果
            searchCache: new Map()
        };
    },
    props: {
        value: {
            default: ''
        },
        options: {
            type: Array,
            default: () => []
        },
        single: {
            type: Boolean,
            default: false
        },
        values: {
            type: String,
            default: 'value'
        },
        labels: {
            type: String,
            default: 'label'
        },
        keys: {
            type: String,
            default: 'value'
        },
        clearables: {
            type: Boolean,
            default: true
        },
        placeholders: {
            type: String,
            default: '请选择'
        },
        width: {
            type: [String, Number],
            default: '100%'
        },
        multiple: {
            type: Boolean,
            default: false
        },
        disable: {
            type: Boolean,
            default: false
        },
        // 虚拟滚动相关配置
        itemHeight: {
            type: Number,
            default: 34
        },
        bufferSize: {
            type: Number,
            default: 5
        },
        // 性能优化配置
        maxDisplayItems: {
            type: Number,
            default: 50
        },
        searchDebounce: {
            type: Number,
            default: 300
        },
        enableCache: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        // 计算实际显示的数据
        displayData() {
            return this.isSearching ? this.searchResults : this.rawData;
        }
    },
    watch: {
        options: {
            handler(newOptions) {
                if (Array.isArray(newOptions)) {
                    this.rawData = [...newOptions];
                    this.updateVisibleItems();
                    // 确保选中项在新数据中也能正确显示
                    this.ensureSelectedValueInDisplay();
                }
            },
            immediate: true,
            deep: true
        },
        value: {
            handler(newValue) {
                this.Datas = newValue;
                // 当值发生变化时，确保选中项在显示列表中
                this.$nextTick(() => {
                    this.ensureSelectedValueInDisplay();
                });
            },
            immediate: true
        }
    },
    methods: {
        // 初始化选择器
        initSelect() {
            this.fixIOSInput();
        },
        
        // 修复iOS输入问题
        fixIOSInput() {
            const u = navigator.userAgent;
            const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
            
            if (isIOS && this.$refs.select && this.$refs.select.$el) {
                const input = this.$refs.select.$el.querySelector('.el-input__inner');
                if (input) {
                    input.addEventListener('input', this.handleIOSInput);
                    this.$once('hook:beforeDestroy', () => {
                        input.removeEventListener('input', this.handleIOSInput);
                    });
                }
            }
        },
        
        // 处理iOS输入事件
        handleIOSInput(event) {
            const value = event.target.value;
            this.handleSearch(value);
        },
        
        // 处理搜索
        handleSearch(query) {
            // 清除之前的定时器
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }
            
            // 如果查询为空，恢复原始数据
            if (!query) {
                this.clearSearch();
                return;
            }
            
            // 防抖处理
            this.searchTimer = setTimeout(() => {
                this.performSearch(query.trim());
            }, this.searchDebounce);
        },
        
        // 执行搜索
        async performSearch(query) {
            if (!query) return;
            
            this.loading = true;
            
            try {
                // 检查缓存
                if (this.enableCache && this.searchCache.has(query)) {
                    this.searchResults = this.searchCache.get(query);
                    this.isSearching = true;
                    this.updateVisibleItems();
                    return;
                }
                
                // 执行搜索
                const lowerQuery = query.toLowerCase();
                this.searchResults = this.rawData.filter(item => {
                    const valueStr = this.getItemValueText(item).toLowerCase();
                    const labelStr = this.getItemLabelText(item).toLowerCase();
                    return valueStr.includes(lowerQuery) || labelStr.includes(lowerQuery);
                });
                
                // 缓存结果
                if (this.enableCache && this.searchResults.length > 0) {
                    this.searchCache.set(query, this.searchResults);
                    // 限制缓存大小
                    if (this.searchCache.size > 100) {
                        const firstKey = this.searchCache.keys().next().value;
                        this.searchCache.delete(firstKey);
                    }
                }
                
                this.isSearching = true;
                this.updateVisibleItems();
                
            } catch (error) {
                console.error('搜索出错:', error);
                this.searchResults = [];
            } finally {
                this.loading = false;
            }
        },
        
        // 清除搜索状态
        clearSearch() {
            this.isSearching = false;
            this.searchResults = [];
            this.updateVisibleItems();
        },
        
        // 更新可见项目
        updateVisibleItems() {
            const sourceData = this.isSearching ? this.searchResults : this.rawData;
            this.visibleItems = sourceData.slice(0, this.maxDisplayItems);
            // 确保选中项在显示列表中
            this.ensureSelectedValueInDisplay();
        },
        
        // 处理下拉框可见性变化
        handleVisibleChange(visible) {
            if (visible) {
                // 打开时确保有数据显示
                if (this.visibleItems.length === 0) {
                    this.updateVisibleItems();
                }
            } else {
                // 关闭时清除搜索状态
                if (this.isSearching) {
                    this.clearSearch();
                }
            }
        },
        
        // 处理选择变化
        handleChange(value) {
            this.$emit('selectChange', value);
            this.$emit('input', value);
        },
        
        // 工具方法
        getItemKey(item) {
            return item[this.keys] || item[this.values] || JSON.stringify(item);
        },
        
        getItemValue(item) {
            return item[this.values] || item;
        },
        
        getItemValueText(item) {
            return item[this.values] || item;
        },
        
        getItemLabelText(item) {
            return item[this.labels] || '';
        },
        
        getOptionLabel(item) {
            const valueText = this.getItemValueText(item);
            const labelText = this.getItemLabelText(item);
            
            if (this.single || !labelText) {
                return valueText;
            }
            return `${valueText}-${labelText}`;
        },
        
        isItemDisabled(item) {
            return item['islock'] === 1 || item.status === 0;
        },
        
        isItemLocked(item) {
            return item['islock'] === 1;
        },
        
        // 确保选中的值在显示选项中
        ensureSelectedValueInDisplay() {
            if (!this.Datas || this.Datas === '' || !this.rawData.length) {
                return;
            }
            
            // 检查当前选中的值是否已经在显示选项中
            const isSelectedInDisplay = this.visibleItems.some(item => 
                this.getItemValue(item) === this.Datas
            );
            
            if (!isSelectedInDisplay) {
                // 查找选中的项
                const selectedItem = this.rawData.find(item => 
                    this.getItemValue(item) === this.Datas
                );
                
                if (selectedItem) {
                    // 将选中项添加到显示列表的开头
                    const newVisibleItems = [selectedItem, ...this.visibleItems];
                    // 保持列表长度不超过maxDisplayItems
                    this.visibleItems = newVisibleItems.slice(0, this.maxDisplayItems);
                }
            }
        }
    },
    
    beforeDestroy() {
        // 清理定时器
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        // 清理缓存
        this.searchCache.clear();
    }
};
</script>

<style scoped>
.virtual-select-container {
    position: relative;
}

.virtual-select {
    width: 100%;
}

.option-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.option-value {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 5px;
}

.option-value.locked {
    color: #8492a6;
}

.option-label {
    color: #8492a6;
    font-size: 13px;
    margin-left: 10px;
}

/* 自定义下拉样式 */
::v-deep .virtual-select-popper {
    max-width: none !important;
}

::v-deep .virtual-select-popper .el-select-dropdown__list {
    max-height: 300px;
    overflow-y: auto;
}

/* 移动端优化 */
@media (max-width: 768px) {
    .option-content {
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
    }
    
    .option-label {
        margin-left: 0;
        font-size: 12px;
    }
}
</style>